import { generateClient, post } from "aws-amplify/api";
import { getUrl } from "aws-amplify/storage";

const client = generateClient();

export async function requestGetConsultantByID(dispatch, payload) {
  try {
    const { id } = payload;

    dispatch({ type: "REQUEST_GET_CONSULTANT" });

    const params = {
      id,
    };

    const res = await client.graphql({
      query: getConsultant,
      variables: params,
    });

    const consultant = res.data.getConsultant;

    if (!consultant) throw new Error("Consultant not found.");

    if (consultant.user) {
      consultant.user.imageUrl = null;
      if (consultant.user.image?.key) {
        const { identityId, key } = consultant.user.image;
        try {
          const urlResult = await getUrl({
            path: `protected/${identityId}/${key}`,
            options: {
              useAccelerateEndpoint: true,
            },
          });
          consultant.user.imageUrl = urlResult.url.href;
        } catch (error) {}
      }
    }

    dispatch({
      type: "GET_CONSULTANT_SUCCESS",
      payload: { consultant },
    });
  } catch (error) {
    dispatch({ type: "GET_CONSULTANT_ERROR", error });
  }
}

export async function requestGetConsultant(dispatch, payload) {
  try {
    const { userID } = payload;

    const request = client.graphql({
      query: consultantsByUser,
      variables: {
        userID,
      },
    });
    dispatch({ type: "REQUEST_GET_CONSULTANT", payload: { request } });

    const res = await request;

    if (res.data.consultantsByUser.items.length === 0) {
      throw new Error("Consultant not found.");
    }

    dispatch({
      type: "GET_CONSULTANT_SUCCESS",
      payload: {
        consultant: res.data.consultantsByUser.items[0],
      },
    });
  } catch (error) {
    dispatch({ type: "GET_CONSULTANT_ERROR", error });
  }
}

export async function requestListConsultant(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_LIST_CONSULTANT" });

    const params = {
      filter: {
        acceptOnline: { eq: true },
        active: { eq: true },
      },
    };

    const res = await client.graphql({
      query: listConsultants,
      variables: params,
    });
    let { items, nextToken: token } = res.data.listConsultants;

    items = await Promise.all(
      items.map(async (item) => {
        let updatedItem = item;
        if (item.user.image?.key) {
          const { identityId, key } = item.user.image;
          try {
            const urlResult = await getUrl({
              path: `protected/${identityId}/${key}`,
              options: {
                useAccelerateEndpoint: true,
              },
            });
            const signedUrl = urlResult.url.href;
            updatedItem = {
              ...updatedItem,
              user: {
                ...updatedItem.user,
                imageUrl: signedUrl,
              },
            };
          } catch (error) {}
        }

        return updatedItem;
      }),
    );
    // Sort by fastest response time (lowest first), push 0/null to end
    items.sort((a, b) => {
      const aResp = a.averageResponse || Infinity;
      const bResp = b.averageResponse || Infinity;
      return aResp - bResp;
    });

    dispatch({
      type: "LIST_CONSULTANT_SUCCESS",
      payload: { items, nextToken: token },
    });
  } catch (error) {
    dispatch({ type: "LIST_CONSULTANT_ERROR", error });
  }
}

export async function requestConsultantTransactionList(dispatch, payload) {
  try {
    const {
      page,
      consultantID,
      limit = 10,
      nextToken = null,
      sortDirection = "DESC",
    } = payload;
    dispatch({ type: "REQUEST_CONSULTANT_TRANSACTIONS", payload: { page } });

    const params = {
      consultantID,
      limit,
      nextToken,
      sortDirection,
    };

    const request = await client.graphql({
      query: transactionsByConsultantCreatedAt,
      variables: params,
    });

    const { items, nextToken: token } =
      request.data.transactionsByConsultantCreatedAt;
    const nextPage = page + 1;

    dispatch({
      type: "SUCCESS_CONSULTANT_TRANSACTIONS",
      payload: {
        items,
        nextToken: token,
        nextPage,
      },
    });
  } catch (error) {
    dispatch({ type: "ERROR_CONSULTANT_TRANSACTIONS", error });
  }
}

export async function requestListNextConsultant(dispatch, payload) {
  try {
    const { nextToken = null } = payload;

    dispatch({ type: "REQUEST_LIST_NEXT_CONSULTANT" });

    const params = {
      acceptOnline: true,
      active: true,
      nextToken,
    };

    const res = await client.graphql({
      query: listConsultants,
      variables: params,
    });

    const { items, nextToken: token } = res.data.listConsultants;

    dispatch({
      type: "LIST_NEXT_CONSULTANT_SUCCESS",
      payload: { items, nextToken: token },
    });
  } catch (error) {
    dispatch({ type: "LIST_NEXT_CONSULTANT_ERROR", error });
  }
}

export async function requestUpdateConsultant(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_UPDATE_CONSULTANT", payload: { request: null } });
    const {
      active,
      acceptCall,
      acceptZoom,
      addServices,
      deleteServices,
      activeDays,
      startTimes,
      endTimes,
      workStartYear,
    } = payload;

    const request = post({
      apiName: "consultant",
      path: "/update",
      options: {
        body: {
          active,
          acceptCall,
          acceptZoom,
          addServices,
          deleteServices,
          activeDays,
          startTimes,
          endTimes,
          workStartYear,
        },
      },
    });
    dispatch({ type: "REQUEST_UPDATE_CONSULTANT", payload: { request } });

    const { body } = await request.response;
    const res = await body.json();

    dispatch({
      type: "UPDATE_CONSULTANT_SUCCESS",
      payload: { data: res.data },
    });
  } catch (error) {
    dispatch({ type: "UPDATE_CONSULTANT_ERROR", error });
  }
}

export async function requestCreateConsultantTemplate(dispatch, payload) {
  try {
    const { text } = payload;

    const request = client.graphql({
      query: createConsultantTemplate,
      variables: {
        input: {
          text,
        },
      },
    });
    dispatch({
      type: "REQUEST_CREATE_CONSULTANT_TEMPLATE",
      payload: { request },
    });

    const res = await request;

    dispatch({
      type: "CREATE_CONSULTANT_TEMPLATE_SUCCESS",
      payload: {
        template: res.data.createConsultantTemplate,
      },
    });
  } catch (error) {
    dispatch({ type: "CREATE_CONSULTANT_TEMPLATE_ERROR", error });
  }
}

export async function requestUpdateConsultantTemplate(dispatch, payload) {
  try {
    const { id, text } = payload;

    const request = client.graphql({
      query: updateConsultantTemplate,
      variables: {
        input: {
          id,
          text,
        },
      },
    });
    dispatch({
      type: "REQUEST_UPDATE_CONSULTANT_TEMPLATE",
      payload: { request },
    });

    const res = await request;

    dispatch({
      type: "UPDATE_CONSULTANT_TEMPLATE_SUCCESS",
      payload: {
        template: res.data.updateConsultantTemplate,
      },
    });
  } catch (error) {
    dispatch({ type: "UPDATE_CONSULTANT_TEMPLATE_ERROR", error });
  }
}

export async function requestListConsultantTemplates(dispatch, payload) {
  try {
    const { page, limit = 10, nextToken = null } = payload;

    dispatch({
      type: "REQUEST_LIST_CONSULTANT_TEMPLATES",
      payload: { request: null },
    });

    if (page > 1) {
      dispatch({ type: "REQUEST_MORE_CONSULTANT_TEMPLATES" });
    } else {
      dispatch({
        type: "REQUEST_LIST_CONSULTANT_TEMPLATES",
        payload: { page },
      });
    }

    const params = { limit, nextToken, sortDirection: "DESC" };

    const res = await client.graphql({
      query: listConsultantTemplates,
      variables: params,
    });

    let { items, nextToken: token } = res.data.listConsultantTemplates;
    const nextPage = page + 1;

    if (items.length === 0) {
      token = null;
    }

    dispatch({
      type: "LIST_CONSULTANT_TEMPLATES_SUCCESS",
      payload: {
        items,
        nextToken: token,
        nextPage,
        append: page > 1,
      },
    });
  } catch (error) {
    dispatch({ type: "LIST_CONSULTANT_TEMPLATES_ERROR", error });
  }
}

export async function requestDeleteConsultantTemplate(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_DELETE_CONSULTANT_TEMPLATES",
      payload: { request: null },
    });
    const { id } = payload;

    const request = client.graphql({
      query: deleteConsultantTemplate,
      variables: {
        input: {
          id,
        },
      },
    });
    dispatch({
      type: "REQUEST_DELETE_CONSULTANT_TEMPLATES",
      payload: { request },
    });

    const res = await request;

    dispatch({
      type: "DELETE_CONSULTANT_TEMPLATES_SUCCESS",
      payload: { deletedID: res.data.deleteConsultantTemplate.id },
    });
  } catch (error) {
    dispatch({ type: "DELETE_CONSULTANT_TEMPLATES_ERROR", error });
  }
}

export async function requestWithdrawConsultantBalance(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_WITHDRAW_CONSULTANT_BALANCE" });
    const { consultantID, total, bank, accountName, accountNumber } = payload;

    const request = post({
      apiName: "consultant",
      path: "/withdraw",
      options: {
        body: {
          consultantID,
          total,
          bank,
          accountName,
          accountNumber,
        },
      },
    });

    const { body } = await request.response;
    const res = await body.json();

    const { data } = res;

    if (!data.success) {
      throw new Error(data.message);
    }

    dispatch({
      type: "SUCCESS_WITHDRAW_CONSULTANT_BALANCE",
      payload: { withdraw: data.transactionID },
    });
  } catch (error) {
    dispatch({ type: "ERROR_WITHDRAW_CONSULTANT_BALANCE", error });
  }
}

export async function resetListTemplateError(dispatch) {
  dispatch({ type: "RESET_ERROR_CONSULTANT_TEMPLATE" });
}

export async function clearCreateTemplate(dispatch) {
  dispatch({ type: "CLEAR_CREATE_TEMPLATE" });
}

export async function clearUpdateConsultant(dispatch) {
  dispatch({ type: "CLEAR_UPDATE_CONSULTANT" });
}

export async function clearDeleteTemplate(dispatch) {
  dispatch({ type: "CLEAR_DELETE_TEMPLATE" });
}

export async function clearConsultant(dispatch) {
  dispatch({ type: "CLEAR_CONSULTANT" });
}
export async function clearWithdrawConsultantBalance(dispatch) {
  dispatch({ type: "CLEAR_WITHDRAW_CONSULTANT_BALANCE" });
}

const listConsultants = /* GraphQL */ `
  query ListConsultants(
    $filter: ModelConsultantFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listConsultants(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        acceptOnline
        active
        acceptCall
        acceptZoom
        acceptMeeting
        startTimes
        endTimes
        activeDays
        user {
          id
          name
          image {
            bucket
            region
            key
            identityId
          }
        }
        services {
          items {
            id
            consultantID
            serviceID
            service {
              id
              name
            }
            createdAt
            updatedAt
          }
          nextToken
        }
        city
        experience
        workStartYear
        certificates {
          name
          level
          year
          issuedBy
        }
        consultationPrice
        callPrice
        audioPrice
        zoomPrice
        lastSeen
        totalRating
        rating
        totalMonthlyConsultation
        totalConsultation
        averageResponse
        topServices
      }
      nextToken
    }
  }
`;

const getConsultant = /* GraphQL */ `
  query GetConsultant($id: ID!) {
    getConsultant(id: $id) {
      id
      acceptOnline
      active
      acceptCall
      acceptZoom
      acceptMeeting
      acceptVoucher
      startTimes
      endTimes
      activeDays
      userID
      city
      experience
      workStartYear
      certificates {
        name
        level
        year
        issuedBy
      }
      consultationPrice
      callPrice
      audioPrice
      zoomPrice
      lastSeen
      totalRating
      rating
      totalMonthlyConsultation
      totalConsultation
      averageResponse
      user {
        id
        name
        image {
          bucket
          region
          key
          identityId
        }
      }
      services {
        items {
          service {
            id
            name
          }
        }
        nextToken
      }
    }
  }
`;

const consultantsByUser = /* GraphQL */ `
  query ConsultantsByUser(
    $userID: ID
    $sortDirection: ModelSortDirection
    $filter: ModelConsultantFilterInput
    $limit: Int
    $nextToken: String
  ) {
    consultantsByUser(
      userID: $userID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        acceptOnline
        active
        acceptCall
        acceptZoom
        acceptMeeting
        acceptVoucher
        startTimes
        endTimes
        activeDays
        userID
        city
        experience
        workStartYear
        certificates {
          name
          level
          year
          issuedBy
        }
        consultationPrice
        callPrice
        audioPrice
        lastSeen
        totalRating
        rating
        lastRating
        totalMonthlyConsultation
        totalConsultation
        lastTotalConsultation
        averageResponse
        lastAverageResponse
        balance
        pendingBalance
        fee
        bankAccount {
          id
          name
          number
          address
        }
        createdAt
        updatedAt
        user {
          id
          identityId
          name
          image {
            bucket
            region
            key
            identityId
          }
          email
          phone
          callBalance
          membership
          subscriptionID
          mainBusinessID
          createdAt
          updatedAt
        }
        services {
          items {
            id
            consultantID
            serviceID
            createdAt
            updatedAt
          }
          nextToken
        }
        transactions {
          nextToken
        }
      }
      nextToken
    }
  }
`;

const transactionsByConsultantCreatedAt = /* GraphQL */ `
  query TransactionsByConsultantCreatedAt(
    $consultantID: ID
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelConsultantTransactionFilterInput
    $limit: Int
    $nextToken: String
  ) {
    transactionsByConsultantCreatedAt(
      consultantID: $consultantID
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        status
        category
        method {
          id
          name
          number
        }
        total
        consultantID
        consultationID
        voucher
        totalDiscount
        fee
        note
        createdAt
        updatedAt
        consultation {
          id
          status
          online
          categoryID
          category {
            id
            name
          }
          description
          consultantID
          transactionID
          userID
          callEnabled
          callBalance
          useGlobalBalance
          complaintID
          expiredAt
          updatedAt
          rating
          review
          createdAt
        }
      }
      nextToken
    }
  }
`;

const createConsultantTemplate = /* GraphQL */ `
  mutation CreateConsultantTemplate($input: CreateConsultantTemplateInput!) {
    createConsultantTemplate(input: $input) {
      id
      text
      createdAt
      updatedAt
    }
  }
`;

const listConsultantTemplates = /* GraphQL */ `
  query ListConsultantTemplates(
    $filter: ModelConsultantTemplateFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listConsultantTemplates(
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        text
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const updateConsultantTemplate = /* GraphQL */ `
  mutation UpdateConsultantTemplate($input: UpdateConsultantTemplateInput!) {
    updateConsultantTemplate(input: $input) {
      id
      text
      createdAt
      updatedAt
    }
  }
`;

const deleteConsultantTemplate = /* GraphQL */ `
  mutation DeleteConsultantTemplate($input: DeleteConsultantTemplateInput!) {
    deleteConsultantTemplate(input: $input) {
      id
      text
      createdAt
      updatedAt
    }
  }
`;
